'use client'

import { type ReactNode, useMemo } from 'react'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  FeatureBadge,
  FormattedMessage,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'

import { BaseCarousel } from '@/components/common/carousel/BaseCarousel'
import { InsideGroupWrapper } from '@/components/common/carousel/InsideGroupWrapper'
import { featureBadgeEventProps } from '@/helpers/featureBadges/analytics'
import { FEATURE_BADGES } from '@/helpers/featureBadges/constants'
import { useReportPageFeatureBadge } from '@/helpers/featureBadges/pageFeatureBadges'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { RecentViewsCandidate } from '@/queries/recentViewsSeedCandidatesQuery'

import { BasedOnRecentViewsReasonLabel } from './BasedOnRecentViewsReasonLabel'
import { NoWrap } from './ui'

/**
 * The buyer-facing surface of the base rail (BP-3104): renders pre-fetched,
 * already-diversity-capped candidates from BP-3100/3103's pipeline. Purely
 * presentational — cold-start (hide when there's nothing to show) and the
 * diversity cap are both decided by the caller (PrefetchedBasedOnRecentViewsCarousel)
 * before this component ever mounts.
 */
export default function BasedOnRecentViewsCarousel({
  candidates,
  carouselVariant,
  insideGroup = false,
  showBadge = false,
}: {
  candidates: RecentViewsCandidate[]
  // A/B arm (BP-3105) — reported on the impression event so the experiment
  // can attribute results per arm. Optional since a caller that isn't
  // running the experiment (e.g. a future non-AB placement) shouldn't be
  // forced to supply one.
  carouselVariant?: string
  insideGroup?: boolean
  // The badge A/B arm, decided on the server
  showBadge?: boolean
}) {
  const { track } = useAnalytics()

  // The flag alone decides the pill; the experiment's start and stop in
  // LaunchDarkly bound its window, not the registry's launchDate
  const pillShown = showBadge
  useReportPageFeatureBadge('recent_views_carousel', pillShown)

  const badgeEventProps = useMemo(
    () => featureBadgeEventProps('recent_views_carousel', { pillShown }),
    [pillShown]
  )

  const reasonTitleByListingId = useMemo(
    () => new Map(candidates.map((c) => [c.listing.id, c.bestSeedTitle])),
    [candidates]
  )

  const handleViewAllClick = () => {
    track('button_click', {
      button_name: 'carousel_view_all',
      carousel_name: 'recent_views',
      screen_name: 'homepage',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      ...(carouselVariant !== undefined && {
        carousel_variant: carouselVariant,
      }),
    })
  }

  const carousel = (
    <BaseCarousel
      data={{
        listings: candidates.map((c) => c.listing),
        total: candidates.length,
      }}
      isLoading={false}
      title={
        <FormattedMessage
          id="Common.Carousel.basedOnRecentViewsTitle"
          values={{
            nowrap: (chunks: ReactNode) => <NoWrap>{chunks}</NoWrap>,
            badge: pillShown ? (
              <FeatureBadge
                variant={FEATURE_BADGES.recent_views_carousel.kind}
              />
            ) : (
              ''
            ),
          }}
        />
      }
      carouselName="recent_views"
      screenName="homepage"
      carousel_variant={carouselVariant}
      onViewAllClick={handleViewAllClick}
      renderCardExtra={(listing) => {
        const title = reasonTitleByListingId.get(listing.id)
        return title ? <BasedOnRecentViewsReasonLabel title={title} /> : null
      }}
    />
  )

  // Both arms carry the badge fields, so the readout can compare them
  const tracked = (
    <NestableAnalyticsProvider value={badgeEventProps}>
      {carousel}
    </NestableAnalyticsProvider>
  )

  return insideGroup ? (
    <InsideGroupWrapper>{tracked}</InsideGroupWrapper>
  ) : (
    tracked
  )
}
